import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, LogOut, Phone } from 'lucide-react';
import { useMyAccounts } from '../account/AccountContext';
import { useLogout } from '../hooks/useLogout';
import { RoleBadge } from '../components/RoleBadge';
import ClaimLinePage from './ClaimLinePage';

/** Which line to work in. A place of its own, not a gate in front of the app.
 *
 *  Someone with a single line never arrives here: `/` sends them straight in. This is
 *  for the person who owns their line and also helps out in others — and it earns the
 *  visit by saying where there is work, which is what the unread count is for.
 *
 *  Rows show the number the line answers on and the caller's role in it, and
 *  deliberately not the owner's email address: that helps you contact them, not choose
 *  between them. */
export default function AccountPickerPage() {
  const { t } = useTranslation();
  const logout = useLogout();
  const { accounts, isLoading } = useMyAccounts();

  if (isLoading) return null;
  // Same screen the `/` resolver shows, rendered rather than redirected to — see
  // AccountResolver on why these two never redirect at each other.
  if (accounts.length === 0) return <ClaimLinePage />;

  return (
    <div className="mx-auto max-w-md px-4 py-10 sm:py-14 space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold">{t('accounts.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('accounts.body')}</p>
      </div>

      <ul className="space-y-2">
        {accounts.map((a) => (
          <li key={a.accountId}>
            <Link
              to={`/a/${a.accountId}`}
              className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 hover:bg-muted"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-foreground">
                    {a.label ?? t('accounts.unnamed')}
                  </span>
                  <RoleBadge role={a.myRole} />
                </div>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Phone className="h-3 w-3 shrink-0" aria-hidden />
                  {a.e164 ?? t('accounts.noNumber')}
                </span>
              </div>
              {a.unread > 0 ? (
                <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                  {a.unread}
                </span>
              ) : null}
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => void logout()}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <LogOut className="h-4 w-4" aria-hidden />
        {t('action.signOut')}
      </button>
    </div>
  );
}
